// Aba "🚑 APH" de Remoções (pedido do Alexandre, 15/10/2026): as APHs (urgência pré-hospitalar)
// cobradas pela SOS — se está na prévia, aconteceu, então aparece como concluída — com o aviso
// "Sem aditivo no IW" quando não há aditivo. Embaixo, as APHs lançadas no Quality que a SOS ainda não cobrou.
// Dados: /api/conciliacao-remocoes (mesma montagem da aba Conciliação).
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { supabase } from '../supabase';
import { ArquivoLink } from '../arquivos';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#6366f1' };
const fmtData = iso => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const fmtValor = v => (typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—');
const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
export const nomeUnidade = u => (u === 'HomeBaby' ? 'Home Baby' : 'Vitalcare');
const ehAPH = r => /INTERCORR|\bAPH\b/i.test(`${r.tipo || ''} ${r.tipo_rm || ''}`);

export default function AphSOS({ registros = [] }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [mes, setMes] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setCarregando(true); setErro('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/conciliacao-remocoes', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
      setDados(j);
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const aph = useMemo(() => (dados?.linhas || []).filter(l => l.categoria === 'APH'), [dados]);
  const meses = useMemo(() => [...new Set(aph.map(l => l.data.slice(0, 7)))].sort().reverse(), [aph]);
  const doMes = useMemo(() => aph.filter(l => !mes || l.data.startsWith(mes)), [aph, mes]);
  const q = busca.trim().toLowerCase();
  const vis = useMemo(() => doMes.filter(l => (!filtro || (filtro === 'sem' ? !l.aditivo : !!l.aditivo))
    && (!q || `${l.paciente} ${l.motivo} ${l.origem} ${l.destino}`.toLowerCase().includes(q))), [doMes, filtro, q]);
  // APH lançada no Quality (Agendadas/Para Agendar) que nenhuma linha da SOS casou e ainda não foi concluída
  const casadas = useMemo(() => new Set(aph.map(l => l.quality?.id).filter(Boolean)), [aph]);
  const semCobranca = useMemo(() => registros.filter(r => ehAPH(r) && !casadas.has(r.id) && r.andamento !== 'Concluído' && r.status !== 'Realizado' && !['Cancelado', 'Negado'].includes(r.status)), [registros, casadas]);
  const soma = arr => arr.reduce((s, x) => s + (x.valor || 0), 0);

  if (carregando) return <div style={{ color: P.muted }}>Lendo as prévias da SOS…</div>;
  if (erro) return <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px' }}>Não foi possível carregar: {erro}</div>;
  const semAditivo = doMes.filter(l => !l.aditivo);
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.8rem' };
  const cartao = (rotulo, valor, sub, cor, k) => (
    <button onClick={() => k !== null && setFiltro(filtro === k ? '' : k)}
      style={{ background: filtro === k && k ? '#eef2ff' : P.white, border: `1px solid ${filtro === k && k ? P.primary : P.border}`, borderRadius: 12, padding: '10px 14px', minWidth: 190, textAlign: 'left', cursor: 'pointer' }}>
      <div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 600 }}>{rotulo}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: cor }}>{valor}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: P.muted }}>{sub}</div>}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: '0.8rem', color: P.muted }}>
        APHs (urgência pré-hospitalar) <strong>cobradas pela SOS Vida</strong> nas prévias recebidas no e-mail do Núcleo. Se está na prévia, aconteceu: aparece como <strong>concluída</strong>.
        Toda remoção precisa de aditivo no IW — a APH é custo dentro do pacote, o aditivo registra esse custo.
        <button onClick={carregar} style={{ marginLeft: 8, border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '3px 8px', cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.74rem' }}><RefreshCw size={12} /> Atualizar</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {cartao('✅ APHs concluídas (SOS)', doMes.length, fmtValor(soma(doMes)), P.text, '')}
        {cartao('⚠ Sem aditivo no IW', semAditivo.length, fmtValor(soma(semAditivo)), '#991b1b', 'sem')}
        {cartao('Com aditivo no IW', doMes.length - semAditivo.length, fmtValor(soma(doMes) - soma(semAditivo)), '#166534', 'com')}
        {semCobranca.length > 0 && cartao('No Quality, sem cobrança da SOS', semCobranca.length, 'ver lista abaixo', '#9a3412', null)}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <select value={mes} onChange={e => setMes(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem' }}>
          <option value="">Todos os meses</option>
          {meses.map(m => <option key={m} value={m}>{m.slice(5)}/{m.slice(0, 4)}</option>)}
        </select>
        <label style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente ou local</span>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Paciente ou local" style={{ padding: '6px 8px 6px 28px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', width: 190 }} />
        </label>
        {filtro && <button onClick={() => setFiltro('')} style={{ ...chip('#f1f5f9', '#334155'), border: `1px solid ${P.border}`, cursor: 'pointer', padding: '4px 10px' }}>limpar filtro ✕</button>}
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
            <thead><tr>{['Data', 'Paciente', 'Trajeto · motivo', 'Valor', 'Situação', 'Aditivo no IW', 'Prévia'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {vis.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: P.muted, padding: 26 }}>Nada neste filtro.</td></tr>}
              {vis.map(l => (
                <tr key={l.id} style={{ background: !l.aditivo ? '#fff7f7' : undefined }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(l.data)}<div style={{ fontSize: '0.7rem', color: P.muted }}>{nomeUnidade(l.unidade)}</div></td>
                  <td style={{ ...td, fontWeight: 700, maxWidth: 240 }}>
                    {l.paciente}
                    <div style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>{[l.convenio, l.identificacao?.numIW && `IW ${l.identificacao.numIW}`].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td style={{ ...td, maxWidth: 280 }}>
                    <div>{l.origem} → {l.destino}</div>
                    <div style={{ fontSize: '0.72rem', color: P.muted }}>{l.motivo}{l.viatura ? ` · ${l.viatura}` : ''}</div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtValor(l.valor)}</td>
                  <td style={td}>
                    <span style={chip('#dcfce7', '#166534')}>✅ Concluída</span>
                    <div style={{ fontSize: '0.7rem', color: P.muted, marginTop: 2 }}>{l.quality ? `no Quality: ${l.quality.aba}` : 'cobrada pela SOS'}</div>
                  </td>
                  <td style={td}>
                    {l.aditivo
                      ? <span style={chip('#dcfce7', '#166534')} title={l.aditivo.origem === 'IW' ? `${l.aditivo.item || ''} · interna ${l.aditivo.interna}/3 · operadora ${l.aditivo.externa}` : l.aditivo.origem}>✓ {l.aditivo.origem === 'IW' ? `IW ${fmtData(l.aditivo.data)}` : l.aditivo.origem}</span>
                      : <span style={chip('#fee2e2', '#991b1b')}>⚠ Sem aditivo no IW</span>}
                  </td>
                  <td style={td}>
                    {l.cobranca?.arquivo?.url
                      ? <ArquivoLink href={l.cobranca.arquivo.url} target="_blank" rel="noreferrer" title={l.cobranca.assunto} style={{ fontSize: '0.72rem', color: '#1e40af' }}>📎 planilha</ArquivoLink>
                      : '—'}
                    <div style={{ fontSize: '0.68rem', color: P.muted }}>{fmtData(l.cobranca?.recebidoEm)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${P.border}`, fontSize: '0.78rem', color: P.muted }}>
          {vis.length} APH(s) · {fmtValor(soma(vis))}
        </div>
      </div>

      {semCobranca.length > 0 && (
        <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, padding: '12px 14px' }}>
          <div style={{ fontWeight: 800, fontSize: '0.85rem', color: P.text, marginBottom: 6 }}>APHs lançadas no Quality que a SOS ainda não cobrou ({semCobranca.length})</div>
          <div style={{ fontSize: '0.75rem', color: P.muted, marginBottom: 8 }}>Ficam aqui até aparecerem numa prévia da SOS (aí viram concluídas sozinhas).</div>
          {semCobranca.map(r => (
            <div key={r.id} style={{ fontSize: '0.8rem', padding: '5px 0', borderTop: '1px solid #f1f5f9' }}>
              <strong>{r.paciente}</strong> · {fmtData(r.data || r.data_agendamento)} · {r.motivo || '—'}
              {r.aditivo_iw !== 'Sim' && <span style={{ ...chip('#fee2e2', '#991b1b'), marginLeft: 6 }}>⚠ Sem aditivo no IW</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
